import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import WSButton from "../../Components/Button/WSButton";
import config from "../../Config/config.json";
import {
  callAPI,
  dateFormat,
  formatIndianCurrency,
  getLocalValue,
  LoacalVariables,
  Method,
} from "../../commonFunctions/CommonFunctions";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import { NotFound } from "../NotFound";
import logo from "../../images/logo.svg";
import { IBranch } from "../DTO/Dto";

const InvPrint: React.FC = () => {
  const { id } = useParams();
  const contentRef = useRef<HTMLDivElement>(null);
  const { showMessage } = useSnackbar();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [branchData, setBranchData] = useState<IBranch>();

  useEffect(() => {
    async function getDataById() {
      try {
        const response: any = await callAPI("CRMInvoice", Method.GET, null, id);
        console.log('branch response', response);

        if (response?.data) {
          setData(response.data);
        }
      } catch (error) {
        showMessage("Something went wrong. Please try again.", "error");
      } finally {
        setLoading(false);
      }
    }

    async function getBranchDetails() {
      try {
        const response: any = await callAPI(
          "Branch",
          Method.GET,
          null,
          getLocalValue(LoacalVariables.Branch)
        );

        if (response?.data) {
          setBranchData(response.data);
        }
      } catch (error) {
        showMessage("Something went wrong. Please try again.", "error");
      }
    }

    if (id) {
      getDataById();
      getBranchDetails();
    }
  }, [id]);

  const handlePrint = useReactToPrint({
    contentRef,
  });

  if (loading)
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );

  if (!data) return <NotFound />;

  const totalAmount = data.quotationItems
    ?.reduce((sum: number, item: any) => sum + Number(item.totalPrice || 0), 0)
    .toFixed(2);

  const taxPercent = data?.tax || 0;
  const taxAmount = ((Number(totalAmount) * (taxPercent / 2)) / 100).toFixed(2);
  const grandTotal = (Number(totalAmount) + Number(taxAmount) * 2).toFixed(2);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      p={4}
      bgcolor="grey.50"
      minHeight="100vh"
    >
      {/* Print Button with better styling */}
      <Box sx={{ width: "100%", maxWidth: "850px", mb: 2, textAlign: "right" }}>
        <WSButton
          id="printBtn"
          variant="contained"
          size="medium"
          label="Print Invoice"
          onClick={handlePrint}
          sx={{
            bgcolor: "#1976d2",
            "&:hover": { bgcolor: "#1565c0" },
            px: 3,
            py: 1,
            borderRadius: 2,
            boxShadow: 2,
          }}
        />
      </Box>

      {/* Printable Content */}
      <Box
        ref={contentRef}
        sx={{
          p: 4,
          width: "100%",
          maxWidth: "850px",
          bgcolor: "white",
          borderRadius: 3,
          boxShadow: 4,
          border: "1px solid #e0e0e0",
        }}
      >
        {/* Header with improved layout */}
        <Grid container justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Grid item xs={6}>
            <Box display="flex" alignItems="center" gap={2}>
              <img
                src={logo}
                alt="Company Logo"
                width={100}
                height="auto"
                style={{ objectFit: "contain" }}
              />
            </Box>
          </Grid>
          <Grid item xs={6} textAlign="right">
            <Typography variant="h6" fontWeight={700} color="error" gutterBottom>
              {branchData?.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {branchData?.address?.addressLine1}
              {branchData?.address?.addressLine2 && `, ${branchData?.address?.addressLine2}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              P.O Box: {branchData?.address?.pincode}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Phone: {branchData?.phoneNumber}
            </Typography>
            {branchData?.gstNumber && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                <strong>GSTIN:</strong> {branchData.gstNumber}
              </Typography>
            )}
          </Grid>
        </Grid>

        <Divider sx={{ my: 3, borderWidth: 1 }} />

        {/* Bill To & Invoice Details Side by Side */}
        <Grid container spacing={4}>
          <Grid item xs={8}>
            <Typography variant="subtitle1" fontWeight={700} color="gray" gutterBottom>
              Bill To
            </Typography>
            <Box sx={{ p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
              <Typography variant="body1" fontWeight={600} gutterBottom>
                {data.customerName}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {data.address || "Address not provided"}
              </Typography>
              {data.state && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Place Of Supply:</strong> {data.state}
                </Typography>
              )}
            </Box>
          </Grid>

          <Grid item xs={4}>
            <Typography variant="subtitle1" fontWeight={700} color="gray" gutterBottom>
              Invoice Details
            </Typography>
            <Box sx={{ p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" fontWeight={600}>Invoice No:</Typography>
                <Typography variant="body2">{data.invoiceNumber}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" fontWeight={600}>Date:</Typography>
                <Typography variant="body2">{dateFormat(data.date, "DD/MM/YYYY")}</Typography>
              </Box>
              {data.paymentTerms?.[0]?.days && (
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" fontWeight={600}>Terms:</Typography>
                  <Typography variant="body2">Net {data.paymentTerms[0].days} days</Typography>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>

        {/* Items Table with better styling */}
        <TableContainer
          component={Paper}
          sx={{
            mt: 4,
            borderRadius: 2,
            border: "1px solid #e0e0e0",
            overflow: "hidden"
          }}
        >
          <Table size="small">
            <TableHead sx={{ bgcolor: "primary.main" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: "white" }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "white" }}>Item & Description</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: "white" }}>
                  Qty
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: "white" }}>
                  Rate (₹)
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: "white" }}>
                  Amount (₹)
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.quotationItems
                ?.slice() // Create a copy to avoid mutating original array
                .sort((a: any, b: any) => b.totalPrice - a.totalPrice) // Sort by totalPrice descending
                .map((item: any, i: number) => (
                  <TableRow
                    key={item.id} // Use item.id instead of index for better key
                    sx={{
                      '&:nth-of-type(odd)': { bgcolor: 'grey.50' },
                      '&:last-child td': { borderBottom: 0 }
                    }}
                  >
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {item.name} {/* Changed from productName to name */}
                      </Typography>
                      {item.description && (
                        <Typography variant="caption" color="text.secondary">
                          {item.description}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" display="block">
                        Unit: {item.unit}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {item.quantity > 0 ? item.quantity : '-'}
                    </TableCell>
                    <TableCell align="right">
                      {item.unitPrice > 0 ? formatIndianCurrency(item.unitPrice) : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        fontWeight={600}
                        color={item.totalPrice > 0 ? 'inherit' : 'text.secondary'}
                      >
                        {item.totalPrice > 0 ? formatIndianCurrency(item.totalPrice) : '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Totals Section with better organization */}
        <Grid container justifyContent="flex-end" mt={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 2, overflow: "hidden" }}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, bgcolor: "grey.100" }}>Sub Total</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, bgcolor: "grey.100" }}>
                      {formatIndianCurrency(totalAmount)}
                    </TableCell>
                  </TableRow>
                  {/* <TableRow>
                    <TableCell>CGST {taxPercent / 2}%</TableCell>
                    <TableCell align="right">
                      {formatIndianCurrency(taxAmount)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>SGST {taxPercent / 2}%</TableCell>
                    <TableCell align="right">
                      {formatIndianCurrency(taxAmount)}
                    </TableCell>
                  </TableRow> */}
                  <TableRow sx={{ bgcolor: "primary.main" }}>
                    <TableCell sx={{ fontWeight: 700, color: "white" }}>Grand Total</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: "white" }}>
                      {formatIndianCurrency(grandTotal)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Grid>
        </Grid>

        {/* Amount in Words */}
        <Box mt={2} p={2} sx={{ bgcolor: "grey.50", borderRadius: 2 }}>
          <Typography variant="body2">
            <strong>Total In Words:</strong> ₹ {numberToWords(Number(grandTotal))} Only
          </Typography>
        </Box>

        {/* Notes Section */}
        {data.notes && (
          <Box mt={3} p={2} sx={{ bgcolor: "warning.light", borderRadius: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Notes
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
              {data.notes}
            </Typography>
          </Box>
        )}

        {/* Bank Details with better layout */}
        <Grid container spacing={3} mt={2}>
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom color="gray">
                Bank Details
              </Typography>
              <Grid container spacing={1}>
                {[
                  { label: "Account Name", value: data.bank?.bankHolderName },
                  { label: "Bank Name", value: data.bank?.bankName },
                  { label: "Account Number", value: data.bank?.accountNumber },
                  { label: "Branch", value: data.bank?.branchName },
                  { label: "IFSC Code", value: data.bank?.ifscCode },
                ].map((item, index) => (
                  item.value && (
                    <Grid item xs={12} key={index}>
                      <Box display="flex">
                        <Typography variant="body2" fontWeight={600} sx={{ minWidth: 120 }}>
                          {item.label}:
                        </Typography>
                        <Typography variant="body2">{item.value}</Typography>
                      </Box>
                    </Grid>
                  )
                ))}
              </Grid>
            </Box>
          </Grid>

          {/* Signature Section */}
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, textAlign: "center" }}>
              <Box sx={{ height: 80, borderBottom: "1px solid #000", mb: 1 }}></Box>
              <Typography variant="body2" fontWeight={600}>
                Authorized Signature
              </Typography>
              <Typography variant="caption" color="text.secondary">
                For {branchData?.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Date: {dateFormat(new Date(), "DD/MM/YYYY")}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Footer */}
        <Divider sx={{ my: 3 }} />
        <Box textAlign="center">
          <Typography variant="caption" color="text.secondary">
            Thank you for your business!
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {branchData?.name} • Phone: {branchData?.phoneNumber} • Email: {branchData?.email || "—"}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default InvPrint;

// Utility function for converting numbers to words
export function numberToWords(num: number): any {
  const a = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const b = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];

  if ((num = num || 0) === 0) return "Zero";
  if (num < 20) return a[num];
  if (num < 100)
    return b[Math.floor(num / 10)] + (num % 10 ? " " + a[num % 10] : "");
  if (num < 1000)
    return (
      a[Math.floor(num / 100)] +
      " Hundred " +
      (num % 100 ? numberToWords(num % 100) : "")
    );
  if (num < 100000)
    return (
      numberToWords(Math.floor(num / 1000)) +
      " Thousand " +
      numberToWords(num % 1000)
    );
  if (num < 10000000)
    return (
      numberToWords(Math.floor(num / 100000)) +
      " Lakh " +
      numberToWords(num % 100000)
    );
  return num.toString();
}
